import React, {useState} from 'react';
import {ArrowRightIcon} from '@heroicons/react-v2/24/outline';

import ModalNew from '../../components/ModalNew';
import Button, {ButtonTypes, ButtonSizes} from '../../components/ButtonNew';
import Link from '../../components/Link';
import {getPersonaCallHref} from '../../util';
import WaveformIcon from './WaveformIcon';

const TABS = {
  FORM: 'form',
  CALL: 'call',
};

function CheckIcon() {
  return (
    <svg className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

export default function MotivationModal({
  isOpen, onClose, isLoggedIn, hasIxpAccess, userUuid,
}) {
  const [activeTab, setActiveTab] = useState(hasIxpAccess ? TABS.CALL : TABS.FORM);

  const personaHref = getPersonaCallHref({src: 'motivation_modal', uuid: userUuid});

  return (
    <ModalNew
      isOpen={isOpen}
      onClose={onClose}
      modalClassName="max-w-4xl overflow-hidden"
    >
      <div className="flex items-stretch">
        {/* Left: tabs + content */}
        <div className="flex-1 min-w-0 flex flex-col px-5 pt-2 pb-5">
          {/* Time pill for logged-out users */}
          {!isLoggedIn && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-1 mb-6 w-fit">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              ~12 min
            </span>
          )}

          {/* Tab switcher — only shown to logged-in users */}
          {isLoggedIn && (
            <div className="flex gap-1 p-1 mb-6 bg-gray-100 rounded-xl w-fit">
              <button
                type="button"
                onClick={() => setActiveTab(TABS.FORM)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === TABS.FORM
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
                Write it up
              </button>
              <button
                type="button"
                onClick={() => setActiveTab(TABS.CALL)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === TABS.CALL
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 15c1.66 0 3-1.34 3-3V6c0-1.66-1.34-3-3-3S9 4.34 9 6v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V6zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-2.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
                Talk it through
              </button>
            </div>
          )}

          {activeTab === TABS.FORM ? (
            <>
              <h2 className="text-2xl font-medium text-gray-800 tracking-tight mb-2">
                Share your interview experience
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Tell us what actually happened. The rounds, questions, and your honest take.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckIcon />
                  {hasIxpAccess ? (
                    <span>
                      {'Add your experience to our '}
                      <Link href="/experiences?src=contribution" className="text-indigo-600 hover:text-indigo-700">experience database</Link>
                    </span>
                  ) : (
                    <span>
                      {'Unlock trial access to our '}
                      <Link href="/experiences?src=contribution" className="text-indigo-600 hover:text-indigo-700">experience database</Link>
                    </span>
                  )}
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckIcon />
                  Help other candidates with your detailed experience
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckIcon />
                  Stay completely anonymous
                </li>
              </ul>

              <Button
                buttonType={ButtonTypes.SKEUOMORPHIC}
                size={ButtonSizes.LARGE}
                onClick={onClose}
                className="w-full group"
              >
                Let&apos;s go
                <ArrowRightIcon className="w-4 h-4 ml-1.5 transition-transform duration-150 group-hover:translate-x-1" strokeWidth={2.2} />
              </Button>
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-1 mb-4 w-fit">
                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
                </svg>
                For FAANG &amp; frontier AI lab interviews
              </span>

              <h2 className="text-2xl font-medium text-gray-800 tracking-tight mb-2">
                Share with our AI interviewer
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Skip the typing. Walk through your rounds in a short voice call.
                We&apos;ll write it up for you.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckIcon />
                  15-minute voice call, no video required
                </li>
                {/* <li className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckIcon />
                  <span>
                    {'Unlock trial access to our '}
                    <Link
                      href="/experiences?src=contribution"
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      experience database
                    </Link>
                  </span>
                </li> */}
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckIcon />
                  Anonymized before publishing
                </li>
              </ul>

              <Button
                buttonType={ButtonTypes.SKEUOMORPHIC}
                size={ButtonSizes.LARGE}
                href={personaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <WaveformIcon />
                <span className="ml-2">Start the call</span>
              </Button>
            </>
          )}

          <p className="text-xs text-gray-400 text-center mt-4 w-64 mx-auto">
            {'Please help us build a supportive community by following our '}
            <Link href="/guidelines" className="text-indigo-600 hover:text-indigo-700">
              Community guidelines
            </Link>
          </p>
        </div>

        {/* Right image — hidden on mobile, flush to modal edge */}
        <div className="hidden md:block w-1/2 flex-shrink-0">
          <img
            src="/images/building_blocks.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </ModalNew>
  );
}
